import React from 'react';

import {endOfDay} from 'date-fns/endOfDay';
import {startOfDay} from 'date-fns/startOfDay';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';

import {useDayCycle, useOnChange} from '@edna/hooks';

import {
  Calendar,
  ECalendarLeadDate,
  ECalendarType,
  TCalendarDate,
  TCalendarType,
  TWeekStart,
  isRangeValue,
} from '../Calendar';
import {Dropdown} from '../Dropdown';
import {TComponentRef, formField, newFormField} from '../FormField';
import {EPlacementY, TPlacementY} from '../types';
import {DateSection} from './DateSection';
import * as S from './style';
import {DEFAULT_WIDTH, THandler} from './types';
import {getDefaultValue, isDatePickerValue} from './utils';

export type TProps = TComponentRef & {
  id?: string;
  className?: string;
  name?: string;
  value?: any;
  disabled?: boolean;
  readOnly?: boolean;
  isInvalid?: boolean;
  onChange?: THandler;
  onFocus?: TEmptyFunction;
  onBlur?: TEmptyFunction;
  placementY?: TPlacementY;
  dataSelector?: string;
  calendarDataSelector?: string;
  now?: Date;
  minDate?: Date | null;
  maxDate?: Date | null;
  calendarType?: TCalendarType;
  inputPlaceholder?: string;
  weekStart?: TWeekStart;
  isTwoMonthsView?: boolean;
  hasAcceptButtons?: boolean;
  hasYearSwitchButton?: boolean;
};

export const DatePicker = React.memo<TProps>(
  ({
    id: propId,
    className,
    name,
    value: propValue,
    disabled,
    readOnly,
    isInvalid,
    onChange,
    onFocus,
    onBlur,
    now: propNow,
    minDate: propMinDate,
    maxDate: propMaxDate,
    calendarType = ECalendarType.DATE,
    isTwoMonthsView = calendarType === ECalendarType.DATE_RANGE,
    hasYearSwitchButton = calendarType === ECalendarType.DATE_RANGE,
    hasAcceptButtons,
    placementY = EPlacementY.bottom,
    dataSelector,
    calendarDataSelector,
    innerRef,
    weekStart,
    inputPlaceholder,
  }) => {
    const id = propId ?? name;

    const cycledNow = useDayCycle();
    const now = propNow ?? cycledNow;

    const propDate = React.useMemo(() => {
      const defaultValue = getDefaultValue(propValue, calendarType);

      return {
        startDate: defaultValue.startDate,
        endDate: defaultValue.endDate,
      };
    }, [propValue, calendarType]);

    const [isOpen, setIsOpen] = React.useState(true);
    const [hoveredDate, setHoveredDate] = React.useState<Date | null>(null);
    const [leadDate, setLeadDate] = React.useState<ECalendarLeadDate>(ECalendarLeadDate.START);
    const [startDate, setStartDate] = React.useState<Date | null>(propDate.startDate);
    const [endDate, setEndDate] = React.useState<Date | null>(propDate.endDate);

    useOnChange(() => {
      setStartDate(propDate.startDate);
      setEndDate(propDate.endDate);
    }, [propDate]);

    const minDate = React.useMemo(
      () =>
        propMinDate ?? startOfDay(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())),
      [propMinDate, now],
    );

    const maxDate = React.useMemo(() => propMaxDate ?? endOfDay(now), [propMaxDate, now]);

    const value = React.useMemo(() => {
      if (calendarType === ECalendarType.DATE) {
        return startDate;
      }

      return {startDate, endDate};
    }, [calendarType, startDate, endDate]);

    const handleCancel = React.useCallback(() => {
      setIsOpen(false);
      setLeadDate(ECalendarLeadDate.START);
      setStartDate(propDate.startDate);
      setEndDate(propDate.endDate);
      setHoveredDate(null);
    }, [propDate]);

    const handleFocus = React.useCallback(() => onFocus?.(), [onFocus]);
    const handleBlur = React.useCallback(() => onBlur?.(), [onBlur]);

    const handleInputChange = React.useCallback(
      (setValue: React.Dispatch<React.SetStateAction<Date | null>>) =>
        (nextInputValue: Date | null) =>
          setValue(nextInputValue),
      [],
    );

    const handleChange = React.useCallback(
      (nextValue: TCalendarDate) => {
        if (!isRangeValue(nextValue)) {
          setStartDate(nextValue);
          setLeadDate(ECalendarLeadDate.START);

          return;
        }

        setStartDate(nextValue.startDate);
        setEndDate(nextValue.endDate);

        setLeadDate(
          leadDate === ECalendarLeadDate.START ? ECalendarLeadDate.END : ECalendarLeadDate.START,
        );
      },
      [leadDate],
    );

    const handleSubmit = React.useCallback(
      (nextDate?: TCalendarDate) => {
        setIsOpen(false);
        setLeadDate(ECalendarLeadDate.START);
        setHoveredDate(null);

        const submitValue = nextDate || (value as TCalendarDate);

        if (isDatePickerValue(submitValue) && !isEqual(propValue, submitValue)) {
          setStartDate(getDefaultValue(submitValue, calendarType).startDate);
          setEndDate(getDefaultValue(submitValue, calendarType).endDate);

          return onChange?.(submitValue);
        }

        setStartDate(propDate.startDate);
        setEndDate(propDate.endDate);
      },
      [onChange, calendarType, propDate, propValue, value],
    );

    return (
      <Dropdown
        onClose={hasAcceptButtons ? handleCancel : handleSubmit}
        disabled={disabled}
        readOnly={readOnly}
        placementY={placementY}
        closeWhen="clickOutsideDropdown"
        headComponent={S.DropdownHead}
        head={
          <S.TextInput
            id={id}
            innerRef={innerRef}
            className={className}
            dataSelector={dataSelector}
            calendarType={calendarType}
            leftChildren={<S.CalendarIcon disabled={disabled} />}
            renderInput={() => (
              <S.DateInput
                isInvalid={isInvalid}
                disabled={disabled}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onClick={() => setIsOpen(true)}
              >
                <DateSection
                  disabled={disabled}
                  calendarType={calendarType}
                  hoveredDate={leadDate === ECalendarLeadDate.START ? hoveredDate : null}
                  value={startDate}
                  minDate={minDate}
                  maxDate={calendarType === ECalendarType.DATE ? maxDate : null}
                  onChange={handleInputChange(setStartDate)}
                  onFocus={() => setLeadDate(ECalendarLeadDate.START)}
                  placeholder={inputPlaceholder}
                />
                {calendarType === ECalendarType.DATE_RANGE && (
                  <>
                    <S.Arrow>→</S.Arrow>
                    <DateSection
                      disabled={disabled}
                      calendarType={calendarType}
                      hoveredDate={leadDate === ECalendarLeadDate.END ? hoveredDate : null}
                      value={endDate}
                      maxDate={maxDate}
                      onChange={handleInputChange(setEndDate)}
                      onFocus={() => setLeadDate(ECalendarLeadDate.END)}
                      placeholder={inputPlaceholder}
                    />
                  </>
                )}
              </S.DateInput>
            )}
          />
        }
        overlayComponent={S.Overlay}
        overlay={
          isOpen ? (
            <Calendar
              now={now}
              minDate={minDate}
              maxDate={maxDate}
              calendarType={calendarType}
              value={value}
              weekStart={weekStart}
              onChange={handleChange}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onHoverChange={setHoveredDate}
              leadDate={leadDate}
              isTwoMonthsView={isTwoMonthsView}
              hasAcceptButtons={hasAcceptButtons}
              hasYearSwitchButton={hasYearSwitchButton}
              dataSelector={calendarDataSelector}
            />
          ) : (
            <></>
          )
        }
      />
    );
  },
);

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

export const DatePickerField = formField<TProps>(FullWidthDatePicker, {
  width: ({calendarType = ECalendarType.DATE}) => DEFAULT_WIDTH[calendarType as ECalendarType],
});

export const NewDatePickerField = newFormField<TProps>(FullWidthDatePicker, {
  width: ({calendarType = ECalendarType.DATE}) => DEFAULT_WIDTH[calendarType as ECalendarType],
});
